import baseStyled, { keyframes, css as baseCss, ThemedCssFunction, ThemedStyledInterface } from 'styled-components';

import { UploadExcelTheme } from './themes/types';
import Button from '@plasma/ui.general.button';

const styled = baseStyled as ThemedStyledInterface<UploadExcelTheme>;
const css = baseCss as ThemedCssFunction<UploadExcelTheme>;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInPopup = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

export const PageSummary = styled.p`
  font-size: 16px;
  color: #555;
  max-width: 800px;
  text-align: center;
  margin: 10px auto 20px auto;
  line-height: 1.6;
  font-weight: 400;
  padding: 0 20px;
`;

export const MainLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 30px;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  padding: 20px;

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: stretch;
  }
`;

export const LeftPanel = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const RightPanel = styled.div`
  flex: 0 0 450px;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const UploadExcel = styled.div`
  width: 100%;
  height: 100%;
  animation: ${fadeIn} 0.6s ease-out;
  overflow-y: auto;
`;

export const UploadTemplateContainer = styled.div`
  ${({ theme }) => css`
    background: ${theme.mode.uploadExcel.uploadTempBGColor || '#ffffff'};
  `}
  border-radius: 12px;
  border: 1px solid rgba(38, 125, 172, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  padding: 20px;
  width: 100%;
  animation: ${fadeIn} 0.8s ease-out;

  &:hover {
    box-shadow: 0 15px 40px rgba(38, 125, 172, 0.15);
    border-color: rgba(38, 125, 172, 0.6);
  }
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  label {
    font-weight: 600;
    color: #444;
    font-size: 14px;
    flex-shrink: 0;
    width: 100px;
  }
`;

export const NormalPlasmaButton = styled(Button)`
  ${({ theme }) => css`
    background-color: ${theme.mode.uploadExcel.normalButtonBGColor} !important;
    border: ${theme.mode.uploadExcel.normalButtonBorderColor} !important;
  `}
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const slideInAlert = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: #f0fdf4;
  border-left: 4px solid #22c55e;
  border-radius: 8px;
  color: #166534;
  font-weight: 500;
  font-size: 14px;
  margin: 15px auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  animation: ${slideInAlert} 0.4s ease-out;
  width: 100%;
  max-width: 500px;
  
  &::before {
    content: '✓';
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    background-color: #22c55e;
    color: white;
    border-radius: 50%;
    margin-right: 12px;
    font-size: 12px;
    font-weight: bold;
  }
`;

export const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: #fef2f2;
  border-left: 4px solid #ef4444;
  border-radius: 8px;
  color: #991b1b;
  font-weight: 500;
  font-size: 14px;
  margin: 15px auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  animation: ${slideInAlert} 0.4s ease-out;
  width: 100%;
  max-width: 500px;
  
  &::before {
    content: '!';
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    background-color: #ef4444;
    color: white;
    border-radius: 50%;
    margin-right: 12px;
    font-size: 14px;
    font-weight: bold;
  }
`;

export const UploadExcelStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    padding: '30px',
    margin: '0',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(31, 38, 135, 0.07)',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(8px)',
    width: '100%',
    height: '100%',
    fontFamily: "'Inter', 'Roboto', Arial, sans-serif",
    transition: 'transform 0.3s ease',
  },
  heading: {
    fontSize: '22px',
    marginBottom: '20px',
    color: '#2c3e50',
    fontWeight: "700",
    letterSpacing: '-0.5px',
    textAlign: 'center' as const,
    width: '100%',
    background: 'linear-gradient(90deg, #267DAC, #33adff)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  list: {
    listStyleType: 'none',
    padding: '0',
    margin: '0',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  listItem: {
    fontSize: '15px',
    padding: '12px 16px',
    borderRadius: '8px',
    background: '#ffffff',
    border: '1px solid #f0f0f0',
    color: '#4a5568',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    transition: 'all 0.2s ease',
    cursor: 'default',
  },
  '@media (max-width: 600px)': {
    container: {
      padding: '20px',
      margin: '15px',
    },
    heading: {
      fontSize: '20px',
      marginBottom: '15px',
    },
    listItem: {
      fontSize: '14px',
      padding: '10px 12px',
    },
  },
};
